import React, { useContext, useState } from 'react';
import {
  Button,
  Column,
  Divider,
  GridContainer,
  H3,
  ModalDialog,
  ModalDialogBody,
  ModalDialogButtons,
  ModalDialogCloseButton,
  ModalDialogContent,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogNav,
  ModalDialogTitle,
  Row,
  SelectionSet,
  SelectionSetInput,
  SelectionSetLabel,
  Span,
  Typography,
  useScreenClass,
} from '@vp/swan';
import PropTypes from 'prop-types';
import { ReplaceTokens, useTranslations } from '@vp/om-translations';
import { useMutation, useQueryClient } from 'react-query';
import { BasicPreloader } from '../../shared/BasicPreloader';
import { cancelItemUsingCancellationService } from '../../../apis/orderUpdateRequestsApi';
import { OrderDetailsContext } from '../../contexts/OrderDetailsContext';
import useShopperProfile from '../../../hooks/useShopperProfile';
import LineItemSlim from './LineItemSlim';
import useSiteData from '../../../hooks/useSiteData';
import { EmbeddableLinkCreator } from '../../shared/EmbeddableLinkCreator';
import { SourcePageContext } from '../../contexts/SourcePageContext';
import { OrderActionsContext } from '../../contexts/OrderActionsContext';
import { useLineItemData } from '../../contexts/LineItemDataProvider';
import { GET_SHOPPER_ORDER_DETAILS } from '../../../constants/reactQueryKeys';
import {
  isItemInCancellation,
  ITEM_CANCEL_REQUEST_PENDING,
} from '../../../util/mapStateToStatus';

/**
 * Expected that this is rendered within an LineItemDataProvider to access item properties
 *
 * This modal is responsible for letting the customer know which items they are
 * attempting to cancel, and communicating whether or not we successfully
 * received their request.
 * @param isModalOpen
 * @param setModalAsOpen
 * @param setCancelButtonEnabled
 * @returns {JSX.Element}
 * @constructor
 */
const ItemCancelModal = ({ isModalOpen, setModalAsOpen }) => {
  const queryClient = useQueryClient();
  const screenClass = useScreenClass();
  const { orderNumber, vistacartTenantName, tenantEnvironment, order } =
    useContext(OrderDetailsContext);
  const profile = useShopperProfile();
  const { sourcePage } = useContext(SourcePageContext);
  const { fetchOrderItemActions } = useContext(OrderActionsContext);
  const [customerCancelMessages, sharedActionMessages] = useTranslations([
    'customerCancelMessages',
    'sharedActionMessages',
  ]);
  const { externalAppNavigation } = useSiteData();
  const navToCustomerCare = externalAppNavigation.customerCareContact;
  const lineItemToCancel = useLineItemData();

  const ContactCareLink = EmbeddableLinkCreator({
    href: navToCustomerCare,
    tabIndex: 0,
  });

  const modalTitle = customerCancelMessages.cancellationRequest;
  const cancellationPrompt = customerCancelMessages.cancelModalWarningMessage;
  const modalConfirmButtonText = customerCancelMessages.cancelItemConfirm;
  const modalDeclineButtonText = customerCancelMessages.cancelItemDecline;

  const modalWidthStyle = screenClass === 'xs' ? {} : { width: '600px' };

  const [selectedConfirmationValues, setSelectedConfirmationValues] = useState({
    checkboxOne: false,
    checkboxTwo: false,
  });

  const shopperEmail = order.email ? order.email : profile?.email;

  /**
   * Mutations are typically used to create/update/delete data or perform server side-effects.
   * For more about mutations: https://tanstack.com/query/v4/docs/guides/mutations
   */
  const {
    mutate: cancelItem,
    isLoading,
    isSuccess,
    isError,
    error,
    reset,
  } = useMutation(async item => {
    await cancelItemUsingCancellationService({
      tenantEnvironment,
      orderNumber,
      itemIds: [item.id],
      vistacartTenantName,
      sourcePage,
    });
  });

  /**
   * Performs an *optimistic update* on the current order in the cache
   * that overwrites its state to reflect its pending cancellation.
   * This gives the user immediate feedback while we refetch their order
   * and orderItemAction data to reflect the actual cancellation status.
   *
   * Disclaimer:
   * This logic must be performed on close of the modal
   * (rather than utilizing the mutation helper methods like onMutate and onSuccess)
   * because the optimistic update would force a re-render of the page
   * which abruptly closes the modal window prematurely and without warning
   */
  const updateItemCancellationStatus = async () => {
    // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
    await queryClient.cancelQueries([GET_SHOPPER_ORDER_DETAILS, orderNumber]);

    // Get the current order from the cache
    const cachedOrder = queryClient.getQueryData([
      GET_SHOPPER_ORDER_DETAILS,
      orderNumber,
    ]);

    if (cachedOrder) {
      // If the order doesn't already reflect the item's cancellation state,
      // overwrite the item's state to reflect that it is pending and store it in cache
      queryClient.setQueryData([GET_SHOPPER_ORDER_DETAILS, orderNumber], {
        ...cachedOrder,
        lineItems: cachedOrder.lineItems.map(item =>
          item.id === lineItemToCancel.id && !isItemInCancellation(item)
            ? {
                ...item,
                states: [{ state: ITEM_CANCEL_REQUEST_PENDING }],
              }
            : item
        ),
      });
    }
    // Refetch the order to pick up the updated item state
    queryClient.invalidateQueries(GET_SHOPPER_ORDER_DETAILS);
    // Update the OrderItemActions which contains our cancellability data.
    await fetchOrderItemActions();
  };

  const onCloseHandler = () => {
    setModalAsOpen(false);
    setSelectedConfirmationValues({
      checkboxOne: false,
      checkboxTwo: false,
    });

    if (isSuccess) {
      updateItemCancellationStatus();
    }

    reset();
  };

  let modalDisplayTitle = modalTitle;

  const columnSpan = modalDeclineButtonText && modalConfirmButtonText ? 6 : 12;

  let modalContent = (
    <>
      <ModalDialogBody>
        <LineItemSlim showItemStatus />
        <Divider mt={7} />
        <H3
          fontSize="standard"
          id="cancellationPrompt"
          mt={3}
          mb={3}
          textAlign="left"
          fontWeight="bold"
        >
          {cancellationPrompt}
        </H3>
        <SelectionSet
          mt={3}
          selectedValues={selectedConfirmationValues}
          onSelectedValuesChange={newSelectedValues =>
            setSelectedConfirmationValues(newSelectedValues)
          }
          skin="simple-column"
          variant="multi-select"
          aria-labelledby="cancellationPrompt"
        >
          {/* The aria-required attribute indicates that user input is required on the element before a form may be submitted.
          Ref: https://vista.design/swan/components/selection-set?tab=Accessibility */}
          <SelectionSetInput value="checkboxOne" aria-required="true">
            <SelectionSetLabel>
              <Span fontSize="small">
                {customerCancelMessages.yesIWantToCancel}
              </Span>
            </SelectionSetLabel>
          </SelectionSetInput>
          <SelectionSetInput value="checkboxTwo" aria-required="true">
            <SelectionSetLabel>
              <Span fontSize="small">
                {customerCancelMessages.understandRequest}
              </Span>
            </SelectionSetLabel>
          </SelectionSetInput>
        </SelectionSet>
      </ModalDialogBody>
      <ModalDialogFooter>
        <ModalDialogButtons>
          <GridContainer>
            <Row>
              <Column span={columnSpan}>
                <Button
                  size="mini"
                  width="full-width"
                  skin="secondary"
                  mb={2}
                  onClick={() => onCloseHandler()}
                >
                  {modalDeclineButtonText}
                </Button>
              </Column>
              <Column span={columnSpan}>
                <Button
                  size="mini"
                  width="full-width"
                  skin="primary"
                  mb={2}
                  onClick={() => {
                    cancelItem(lineItemToCancel);
                  }}
                  disabled={
                    !(
                      !!selectedConfirmationValues.checkboxOne &&
                      !!selectedConfirmationValues.checkboxTwo
                    )
                  }
                >
                  {modalConfirmButtonText}
                </Button>
              </Column>
            </Row>
          </GridContainer>
        </ModalDialogButtons>
      </ModalDialogFooter>
    </>
  );

  if (isSuccess) {
    modalDisplayTitle = customerCancelMessages.thanksRequestReceived;
    modalContent = (
      <>
        <ModalDialogBody align="center">
          <Typography fontSize="small" textAllCaps>
            {customerCancelMessages.whatsNext}
          </Typography>
          <Typography mt={6}>
            {customerCancelMessages.weAreTryingToCancel}
          </Typography>
          <Typography mt={4}>
            <ReplaceTokens
              tokenizedString={customerCancelMessages.pleaseCheckYourEmail}
              substitutions={{ email: shopperEmail }}
            />
          </Typography>
          <Divider mt={6} mb={6} />
          <Typography>
            <ReplaceTokens
              tokenizedString={customerCancelMessages.questionsContact}
              substitutions={{
                ContactCareLink,
              }}
            />
          </Typography>
        </ModalDialogBody>
        <ModalDialogFooter>
          <ModalDialogButtons>
            <Button
              size="mini"
              skin="secondary"
              width="full-width"
              mb={2}
              onClick={() => {
                onCloseHandler();
              }}
            >
              {sharedActionMessages.close}
            </Button>
          </ModalDialogButtons>
        </ModalDialogFooter>
      </>
    );
  }

  if (isError) {
    // assumes error is of type {@link CancelRequestError}
    const isRetryable = error.retryable ? error.retryable : false;

    modalDisplayTitle = isRetryable
      ? customerCancelMessages.sorryTryAgain
      : customerCancelMessages.sorryItemCannotBeCancelled;

    const errorText = isRetryable
      ? customerCancelMessages.cancellationRequestFailedContentRetryable
      : customerCancelMessages.cancellationRequestFailedContentNotRetryable;

    modalContent = (
      <>
        <ModalDialogBody align="center">
          <Typography mt={6}>
            <ReplaceTokens
              tokenizedString={errorText}
              substitutions={{
                ContactCareLink,
              }}
            />
          </Typography>
          <Divider mt={6} mb={6} />
        </ModalDialogBody>
        <ModalDialogFooter>
          <ModalDialogButtons>
            <Button
              size="mini"
              skin="secondary"
              width="full-width"
              mb={2}
              onClick={() => {
                onCloseHandler();
              }}
            >
              {sharedActionMessages.close}
            </Button>
          </ModalDialogButtons>
        </ModalDialogFooter>
      </>
    );
  }

  if (isLoading) {
    // TODO: add accessibleText prop with "Loading" translation to support a11y
    modalContent = <BasicPreloader isCentered />;
  }

  return (
    <ModalDialog
      isOpen={isModalOpen}
      onRequestDismiss={() => {
        onCloseHandler();
      }}
      onlyRenderWhenOpen
    >
      <ModalDialogContent style={modalWidthStyle}>
        <ModalDialogNav>
          <ModalDialogCloseButton accessibleText={sharedActionMessages.close} />
        </ModalDialogNav>
        <ModalDialogHeader>
          <ModalDialogTitle align="center">
            {modalDisplayTitle}
          </ModalDialogTitle>
        </ModalDialogHeader>
        {modalContent}
      </ModalDialogContent>
    </ModalDialog>
  );
};

ItemCancelModal.propTypes = {
  isModalOpen: PropTypes.bool,
  setModalAsOpen: PropTypes.func,
};

export default ItemCancelModal;
